import { Link } from "react-router-dom";
import Logo from "./Logo";
import { scrollToSection } from "@/lib/smoothScroll";
import { SubsectionHeader, BodyText, Caption } from "@/components/ui/typography";
import { ThemeToggleSimple } from "@/components/ui/theme-toggle";

export const Footer = () => {
  return (
    <footer className="border-t border-border bg-background">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-10 md:py-12 lg:py-16 xl:py-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-8 md:gap-10 mb-10 md:mb-14 text-center sm:text-left">
            {/* Brand Column */}
            <div className="sm:col-span-2 lg:col-span-1 flex flex-col items-center sm:items-start">
              <Logo size="md" className="mb-3" />
              <Caption className="mb-4 text-center sm:text-left">
                SendFeedback helps companies collect truly anonymous employee feedback, identify retention risks early, and build stronger workplace culture—before their best people decide to leave.
              </Caption>
            </div>
            
            {/* Product Column */}
            <div>
              <SubsectionHeader className="mb-3 md:mb-4">Product</SubsectionHeader>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li>
                  <a 
                    href="/#features" 
                    onClick={(e) => {
                      if (window.location.pathname === '/') {
                        e.preventDefault();
                        scrollToSection('features');
                      }
                    }}
                    className="hover:text-foreground transition-colors inline-block py-1"
                  >
                    Features
                  </a>
                </li>
                <li>
                  <a 
                    href="/#how-it-works" 
                    onClick={(e) => {
                      if (window.location.pathname === '/') {
                        e.preventDefault();
                        scrollToSection('how-it-works');
                      }
                    }}
                    className="hover:text-foreground transition-colors inline-block py-1"
                  >
                    How It Works
                  </a>
                </li>
                <li>
                  <Link to="/pricing" className="hover:text-foreground transition-colors inline-block py-1">
                    Pricing
                  </Link>
                </li>
              </ul>
            </div>

            {/* Solutions Column */}
            <div>
              <SubsectionHeader className="mb-3 md:mb-4">Solutions</SubsectionHeader>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li><Link to="/solutions/hr-teams" className="hover:text-foreground transition-colors inline-block py-1">For HR Teams</Link></li>
                <li><Link to="/solutions/remote-teams" className="hover:text-foreground transition-colors inline-block py-1">For Remote Companies</Link></li>
                <li><Link to="/solutions/startups" className="hover:text-foreground transition-colors inline-block py-1">For Startups</Link></li>
                <li><Link to="/solutions/enterprise" className="hover:text-foreground transition-colors inline-block py-1">For Enterprise</Link></li>
                <li><Link to="/solutions/employee-retention" className="hover:text-foreground transition-colors inline-block py-1">Employee Retention</Link></li>
                <li><Link to="/solutions/culture-building" className="hover:text-foreground transition-colors inline-block py-1">Culture Building</Link></li>
              </ul>
            </div>

            {/* Compare Column */}
            <div>
              <SubsectionHeader className="mb-3 md:mb-4">Compare</SubsectionHeader>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li><Link to="/compare/surveymonkey-alternative" className="hover:text-foreground transition-colors inline-block py-1">vs SurveyMonkey</Link></li>
                <li><Link to="/compare/culture-amp-alternative" className="hover:text-foreground transition-colors inline-block py-1">vs Culture Amp</Link></li>
                <li><Link to="/compare/traditional-surveys" className="hover:text-foreground transition-colors inline-block py-1">vs Traditional Surveys</Link></li>
                <li><Link to="/compare/anonymous-email" className="hover:text-foreground transition-colors inline-block py-1">vs Anonymous Email</Link></li>
                <li><Link to="/compare/why-anonymous-feedback" className="hover:text-foreground transition-colors inline-block py-1">Why Anonymous Works</Link></li>
              </ul>
            </div>

            {/* Company Column */}
            <div>
              <SubsectionHeader className="mb-3 md:mb-4">Company</SubsectionHeader>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li><Link to="/about" className="hover:text-foreground transition-colors inline-block py-1">About Us</Link></li>
                <li><Link to="/contact" className="hover:text-foreground transition-colors inline-block py-1">Contact</Link></li>
                <li><Link to="/how-anonymity-works" className="hover:text-foreground transition-colors inline-block py-1">How Anonymity Works</Link></li>
              </ul>
            </div>
            
            {/* Legal & Resources Column */}
            <div>
              <SubsectionHeader className="mb-3 md:mb-4">Resources</SubsectionHeader>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li><Link to="/help" className="hover:text-foreground transition-colors inline-block py-1">Help Center</Link></li>
                <li><Link to="/track" className="hover:text-foreground transition-colors inline-block py-1">Track Feedback</Link></li>
                <li><Link to="/privacy-policy" className="hover:text-foreground transition-colors inline-block py-1">Privacy Policy</Link></li>
                <li><Link to="/terms-of-service" className="hover:text-foreground transition-colors inline-block py-1">Terms of Service</Link></li>
                <li><Link to="/refund-policy" className="hover:text-foreground transition-colors inline-block py-1">Refund Policy</Link></li>
                <li>
                  <a 
                    href="/#faq" 
                    onClick={(e) => {
                      if (window.location.pathname === '/') {
                        e.preventDefault();
                        scrollToSection('faq');
                      }
                    }}
                    className="hover:text-foreground transition-colors inline-block py-1"
                  >
                    FAQ
                  </a>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Bottom Bar */}
          <div className="pt-6 md:pt-8 border-t border-border flex flex-col sm:flex-row justify-between items-center gap-4">
            <Caption className="text-center sm:text-left">
              © {new Date().getFullYear()} SendFeedback.ai. All rights reserved.
            </Caption>
            <div className="flex items-center gap-4 sm:gap-6 flex-wrap justify-center sm:justify-end">
              <Link to="/privacy-policy" className="text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors">Privacy</Link>
              <Link to="/terms-of-service" className="text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors">Terms</Link>
              <Link to="/refund-policy" className="text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors">Refunds</Link>
              <Link to="/cookie-policy" className="text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors">Cookies</Link>
              <a href="https://stats.uptimerobot.com/ex9N9VOUU4" target="_blank" rel="noopener noreferrer" className="text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors">Uptime</a>
              <ThemeToggleSimple />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
